const TRIGGER_SELECTOR = '[data-add-to-cart-trigger]'
const FORM_SELECTOR = '[data-add-to-cart-form]'
const SUCCESS_SELECTOR = '[data-add-to-cart-success]'
const CART_COUNTER_SELECTOR = '[data-cart-counter]'

export default class AddToCartHandler {
  constructor(remoteModal) {
    this.remoteModal = remoteModal
    this.isSubmitting = false

    this.handleClick = this.handleClick.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  init() {
    if (!this.remoteModal?.isAvailable) {
      return
    }

    document.addEventListener('click', this.handleClick)
    document.addEventListener('submit', this.handleSubmit)
  }

  async handleClick(event) {
    if (!(event.target instanceof Element)) {
      return
    }

    const trigger = event.target.closest(TRIGGER_SELECTOR)

    if (!(trigger instanceof HTMLElement)) {
      return
    }

    /*
     * Een knop binnen een add-to-cartformulier wordt afgehandeld
     * door handleSubmit(). Zo blijft native form-validatie werken.
     */
    if (trigger.closest(FORM_SELECTOR)) {
      return
    }

    const url = trigger.dataset.addToCartUrl
    const productId = trigger.dataset.productId

    if (!url || !productId) {
      return
    }

    event.preventDefault()

    const body = new FormData()

    body.set('productId', productId)

    await this.sendRequest({
      url,
      body,
      trigger,
    })
  }

  async handleSubmit(event) {
    if (!(event.target instanceof HTMLFormElement)) {
      return
    }

    const form = event.target

    if (!form.matches(FORM_SELECTOR)) {
      return
    }

    event.preventDefault()

    const url = form.action

    if (!url) {
      return
    }

    const trigger = event.submitter instanceof HTMLElement ? event.submitter : form.querySelector('[type="submit"]')

    /*
     * FormData neemt productId, amount, opties en de CSRF-token mee.
     */
    const body = new FormData(form)

    await this.sendRequest({
      url,
      body,
      trigger,
    })
  }

  async sendRequest({ url, body, trigger }) {
    if (this.isSubmitting) {
      return
    }

    this.isSubmitting = true
    this.setTriggerBusy(trigger, true)

    /*
     * Bij een formulier eerst FormData maken en daarna pas de
     * modalinhoud vervangen door de loading-state.
     */
    this.remoteModal.openLoading()

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body,
      })

      /*
       * Een 422 kan later gebruikt worden voor een formulier
       * met validatiefouten. De HTML moet dan wel gewoon getoond worden.
       */
      if (!response.ok && response.status !== 422) {
        throw new Error(`Add-to-cart request failed with status ${response.status}`)
      }

      const html = await response.text()

      this.remoteModal.setContent(html)
      this.handleRenderedContent()
    } catch (error) {
      console.error(error)

      this.remoteModal.showError()
    } finally {
      this.isSubmitting = false
      this.setTriggerBusy(trigger, false)
    }
  }

  handleRenderedContent() {
    const successElement = this.remoteModal.element?.querySelector(SUCCESS_SELECTOR)

    /*
     * Geen succes-element betekent dat de response waarschijnlijk
     * het configuratieformulier bevat.
     */
    if (!(successElement instanceof HTMLElement)) {
      return
    }

    const cartAmount = successElement.dataset.cartAmount

    if (cartAmount === undefined) {
      return
    }

    document.querySelectorAll(CART_COUNTER_SELECTOR).forEach((counter) => {
      counter.textContent = cartAmount
    })

    window.dispatchEvent(
      new CustomEvent('cart.updated', {
        detail: {
          amount: Number(cartAmount),
        },
      }),
    )

    window.dispatchEvent(
      new CustomEvent('cart.line.added', {
        detail: {
          line: {
            productSku: successElement.dataset.productSku,
            name: successElement.dataset.itemName,
            brand: successElement.dataset.itemBrand,
            category: successElement.dataset.itemCategory,
            ga4ItemId: successElement.dataset.ga4ItemId,
            ga4Categories: successElement.dataset.ga4Categories,
            variant: successElement.dataset.itemVariant,
            price: Number(successElement.dataset.itemPrice),
            quantity: Number(successElement.dataset.itemQuantity),
          },
        },
      }),
    )
  }

  setTriggerBusy(trigger, isBusy) {
    if (!(trigger instanceof HTMLElement)) {
      return
    }

    trigger.setAttribute('aria-busy', String(isBusy))

    if (trigger instanceof HTMLButtonElement || trigger instanceof HTMLInputElement) {
      trigger.disabled = isBusy
    }

    if (!isBusy) {
      trigger.removeAttribute('aria-busy')
    }
  }
}
